/**
 * Performance monitoring utilities for Vue 3 application
 * Helps track bundle size, component performance, and memory usage
 */

// Performance metrics collection
const performanceMetrics = {
  componentRenderTimes: new Map(),
  memoryUsage: [],
  bundleSize: null
}

/**
 * Track component render performance
 */
export function trackComponentRender(componentName, renderTime) {
  if (!performanceMetrics.componentRenderTimes.has(componentName)) {
    performanceMetrics.componentRenderTimes.set(componentName, [])
  }
  
  const times = performanceMetrics.componentRenderTimes.get(componentName)
  times.push(renderTime)
  
  // Keep only last 50 measurements
  if (times.length > 50) {
    times.shift()
  }
  
  // Log slow renders in development
  if (process.env.NODE_ENV === 'development' && renderTime > 16) {
    console.warn(`🐌 Slow render detected: ${componentName} took ${renderTime.toFixed(2)}ms`)
  }
}

/**
 * Track memory usage
 */
export function trackMemoryUsage() {
  if (performance.memory) {
    const memory = {
      timestamp: Date.now(),
      used: performance.memory.usedJSHeapSize,
      total: performance.memory.totalJSHeapSize,
      limit: performance.memory.jsHeapSizeLimit
    }
    
    performanceMetrics.memoryUsage.push(memory)
    
    // Keep only last 100 measurements
    if (performanceMetrics.memoryUsage.length > 100) {
      performanceMetrics.memoryUsage.shift()
    }
    
    // Warn about high memory usage
    const usagePercent = (memory.used / memory.limit) * 100
    if (usagePercent > 80) {
      console.warn(`⚠️ High memory usage: ${usagePercent.toFixed(1)}%`)
    }
  }
}

/**
 * Get performance report
 */
export function getPerformanceReport() {
  const report = {
    componentPerformance: {},
    memoryUsage: performanceMetrics.memoryUsage.slice(-10), // Last 10 measurements
    recommendations: []
  }
  
  // Analyze component performance
  for (const [componentName, times] of performanceMetrics.componentRenderTimes) {
    const avgTime = times.reduce((a, b) => a + b, 0) / times.length
    const maxTime = Math.max(...times)
    
    report.componentPerformance[componentName] = {
      averageRenderTime: avgTime.toFixed(2),
      maxRenderTime: maxTime.toFixed(2),
      renderCount: times.length
    }
    
    // Add recommendations for slow components
    if (avgTime > 16) {
      report.recommendations.push({
        type: 'slow_component',
        component: componentName,
        message: `Consider optimizing ${componentName} - average render time: ${avgTime.toFixed(2)}ms`
      })
    }
  }
  
  return report
}

/**
 * Vue 3 composable for performance tracking
 */
export function usePerformanceTracking(componentName) {
  const startTime = performance.now()
  
  // Track render time
  const trackRender = () => {
    const renderTime = performance.now() - startTime
    trackComponentRender(componentName, renderTime)
  }
  
  // Track memory usage periodically
  const startMemoryTracking = () => {
    setInterval(trackMemoryUsage, 30000) // Every 30 seconds
  }
  
  return {
    trackRender,
    startMemoryTracking,
    getPerformanceReport
  }
}

/**
 * Bundle size analyzer (development only)
 */
export function analyzeBundleSize() {
  if (process.env.NODE_ENV === 'development') {
    // This would typically be used with webpack-bundle-analyzer
    console.log('📦 Bundle analysis available in development mode')
    console.log('Run: npm run build -- --report to see bundle analysis')
  }
}

// Auto-start memory tracking in development
if (process.env.NODE_ENV === 'development') {
  setInterval(trackMemoryUsage, 30000)
}
